import * as React from 'react';
import { useEffect } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '../../lib/utils';
import { Button } from '../Button';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { Avatar } from '../Avatar';
import { Tooltip } from '../Tooltip';
import { ConditionalWrapper } from '../../lib/ConditionalWrapper';
import type { CarouselProps, CarouselItemProps } from './types';

function isCarouselItem(
  element: React.ReactNode,
): element is React.ReactElement<React.ComponentProps<typeof CarouselItem>> {
  return React.isValidElement(element) && element.type === CarouselItem;
}

export const Carousel = React.forwardRef<HTMLDivElement, CarouselProps>(
  (
    {
      children,
      className,
      buttonVariant = { variant: 'outline', size: 'sm' },
      activeIndex = 0,
      showStepperNavigation = false,
      buttonPrevChild = '',
      buttonNextChild = '',
      buttonNextRef,
      buttonPrevRef,
      onNext = () => true,
      onPrev = () => true,
      onIndexChange = () => {},
    },
    ref,
  ) => {
    const [index, setIndex] = React.useState(activeIndex);
    const count = React.Children.count(children);

    useEffect(() => {
      onIndexChange(index);
    }, [index, onIndexChange]);

    const prev = () => {
      if (onPrev(index)) {
        setIndex((i) => i - 1);
      }
    };
    const next = () => {
      if (onNext(index)) {
        setIndex((i) => i + 1);
      }
    };

    const showPrevButton =
      !showStepperNavigation || (showStepperNavigation && index > 0);

    return (
      <div ref={ref} className='eye:w-full'>
        {showStepperNavigation && count > 1 && (
          <nav
            aria-label='Carousel steps'
            className='eye:mb-12 eye:flex eye:items-center'
          >
            {React.Children.map(children, (child, i) => {
              const active = i === index;
              let tooltipContent = '';

              if (isCarouselItem(child)) {
                tooltipContent = child.props.tooltipContent || '';
              }

              return (
                <React.Fragment key={i}>
                  <ConditionalWrapper
                    condition={!!tooltipContent}
                    wrapper={(children) => (
                      <Tooltip content={tooltipContent}>
                        <span>{children}</span>
                      </Tooltip>
                    )}
                  >
                    <Avatar
                      aria-current={active ? 'step' : false}
                      className={cn(
                        active
                          ? 'eye:bg-black eye:text-white'
                          : 'hover:eye:bg-black eye:bg-grey-disabled eye:text-black',
                      )}
                    >
                      {i + 1}
                    </Avatar>
                  </ConditionalWrapper>

                  {i < count - 1 && (
                    <div
                      aria-hidden='true'
                      className='eye:h-[2px] eye:flex-1 eye:bg-black'
                    />
                  )}
                </React.Fragment>
              );
            })}
          </nav>
        )}

        <div className={cn('eye:w-full eye:overflow-x-hidden', className)}>
          <div
            className={`eye:flex`}
            style={{ transform: `translateX(-${index * 100}%)` }}
          >
            {React.Children.map(children, (child) => (
              <div className='eye:flex eye:h-full eye:w-full eye:min-w-full eye:shrink-0 eye:basis-full eye:items-center eye:justify-center'>
                {child}
              </div>
            ))}
          </div>
        </div>

        {count > 1 && (
          <div
            className={cn(
              'eye:my-12 eye:flex eye:items-center',
              showPrevButton ? 'eye:justify-between' : 'eye:justify-end',
            )}
          >
            {showPrevButton && (
              <Button
                ref={buttonPrevRef}
                disabled={index === 0}
                variant={buttonVariant?.variant}
                size={buttonVariant?.size}
                onClick={prev}
              >
                {buttonPrevChild && (
                  <span className='eye:order-2'>{buttonPrevChild}</span>
                )}
                <IconChevronLeft className='eye:order-1' stroke={2} />
              </Button>
            )}
            {!showStepperNavigation && (
              <span>
                {index + 1} / {count}
              </span>
            )}
            <Button
              ref={buttonNextRef}
              disabled={index === count - 1}
              variant={buttonVariant?.variant}
              size={buttonVariant?.size}
              onClick={next}
            >
              {buttonNextChild && (
                <span className='eye:order-1'>{buttonNextChild}</span>
              )}
              <IconChevronRight className='eye:order-2' stroke={2} />
            </Button>
          </div>
        )}
      </div>
    );
  },
);
Carousel.displayName = 'Carousel';

export const CarouselItem = React.forwardRef<HTMLDivElement, CarouselItemProps>(
  ({ asChild, className, ...props }, ref) => {
    const Comp = asChild ? Slot : 'div';
    return (
      <Comp
        ref={ref}
        className={cn(
          'eye:flex eye:h-full eye:w-full eye:items-center eye:justify-center eye:p-4',
          className,
        )}
        {...props}
      />
    );
  },
);
CarouselItem.displayName = 'CarouselItem';
